import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { IndianRupee, Mail, Phone, RefreshCw } from '../../components/icons';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { PageHeader } from '../../components/page-header';
import { Modal } from '../../components/modal';
import { EmptyState } from '../../components/empty-state';
import { extractErr } from '../../api/error-message';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useUrlTab } from '../../services/attention';

const PER_PAGE = 15;

// Wired to GET /admin/refunds + POST /admin/refunds/:id/refund.
//
// Items on paid orders that the seller rejected or an admin cancelled. Nothing
// is refunded until someone clicks Refund here — the customer's own
// cancellations refund automatically and never appear on this page.

interface RefundRow {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'REFUNDED' | 'FAILED' | 'SUPERSEDED';
  source: 'SELLER_REJECTED' | 'ADMIN_CANCELLED' | 'SYSTEM_CANCELLED';
  reason: string | null;
  suggestedAmount: number;
  refundedAmount: number | null;
  gatewayRefundId: string | null;
  failureReason: string | null;
  adminNote: string | null;
  processedAt: string | null;
  createdAt: string;
  order?: { id: string; orderNumber: string; status: string; totalAmount: number; placedAt: string };
  item?: {
    id: string;
    title: string;
    variant: string | null;
    sku: string | null;
    imageUrl: string | null;
    qty: number;
    itemStatus: string;
    lineTotal: number;
    seller: { id: string; name: string };
  };
  customer?: { id: string; name: string | null; phone: string; email: string | null };
  payment?: {
    id: string;
    amount: number;
    status: string;
    method: string;
    gatewayPaymentId: string | null;
    remainingRefundable: number;
  };
}

const STATUS_TABS = ['PENDING', 'FAILED', 'REFUNDED', 'SUPERSEDED', 'ALL'] as const;
type StatusKey = (typeof STATUS_TABS)[number];

const TAB_LABEL: Record<StatusKey, string> = {
  PENDING: 'To refund',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  SUPERSEDED: 'Covered',
  ALL: 'All',
};

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'badge-warning',
  PROCESSING: 'badge-info',
  REFUNDED: 'badge-success',
  FAILED: 'badge-danger',
  SUPERSEDED: '',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  PROCESSING: 'Processing',
  REFUNDED: 'Refunded',
  FAILED: 'Failed',
  SUPERSEDED: 'Covered by full refund',
};

const SOURCE_LABEL: Record<RefundRow['source'], string> = {
  SELLER_REJECTED: 'Seller rejected',
  ADMIN_CANCELLED: 'Admin cancelled',
  SYSTEM_CANCELLED: 'System cancelled',
};

const inr = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export function RefundsPage() {
  // In the URL (?tab=) so the dashboard / sidebar can open a specific queue.
  const [status, setStatus] = useUrlTab<StatusKey>(STATUS_TABS, 'PENDING');
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<RefundRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ data: RefundRow[] }>('/admin/refunds', { params: { status } });
      setRows(data.data);
      setPage(1);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [status]);

  // The whole tab is loaded, so search runs here, over every column the table
  // shows: product, variant, seller, customer contact, order, payment id, why,
  // amount, status and the dates.
  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.item?.title,
    r.item?.variant,
    r.item?.sku,
    r.item ? `Qty ${r.item.qty}` : null,
    r.item?.seller.name,
    r.customer?.name || `Customer #${r.customer?.id ?? ''}`,
    r.customer?.phone,
    r.customer?.email,
    r.order?.orderNumber,
    r.order ? new Date(r.order.placedAt).toLocaleDateString('en-IN') : null,
    r.order ? inr(r.order.totalAmount) : null,
    r.payment?.gatewayPaymentId,
    SOURCE_LABEL[r.source],
    r.reason,
    new Date(r.createdAt).toLocaleString('en-IN'),
    inr(r.refundedAmount ?? r.suggestedAmount),
    STATUS_LABEL[r.status] ?? r.status,
    r.failureReason,
    r.gatewayRefundId,
    r.processedAt ? new Date(r.processedAt).toLocaleString('en-IN') : null,
    r.adminNote,
  ]);
  useEffect(() => { setPage(1); }, [query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const owed = status === 'PENDING' ? rows.reduce((s, r) => s + r.suggestedAmount, 0) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Refunds"
        description="Paid items the seller rejected or an admin cancelled. Customer cancellations refund automatically."
        actions={
          owed != null && rows.length > 0 ? (
            <div className="card px-4 py-2.5 text-right">
              <p className="text-[12.5px] font-semibold text-warning-ink">Owed to customers</p>
              <p className="font-tabular text-[20px] font-semibold text-ink-900">{inr(owed)}</p>
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setStatus(t)}
            aria-pressed={status === t}
            className="fchip"
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by product, customer, phone, order, reason, amount or status"
        resultCount={filtered.length}
      />

      <div className="panel overflow-hidden">
        {loading ? (
          <SkeletonRows cols={6} />
        ) : rows.length > 0 && filtered.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for “${query.trim()}”`}
            body="Try a customer name, phone, order number or product."
            action={
              <button type="button" onClick={() => setQuery('')} className="btn btn-secondary btn-sm">
                Clear search
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="currency_rupee"
            title={status === 'PENDING' ? 'Nothing waiting to be refunded' : 'No refunds in this view'}
            body={
              status === 'PENDING'
                ? 'Items appear here when a seller rejects or an admin cancels part of a paid order.'
                : 'Try another filter above.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table min-w-[960px]">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Customer</th>
                  <th>Order</th>
                  <th>Why</th>
                  <th className="!text-right">Amount</th>
                  <th>Status</th>
                  <th className="!text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.id} className="[&>td]:align-top">
                    <td>
                      <div className="flex gap-3">
                        {r.item?.imageUrl ? (
                          <img src={r.item.imageUrl} alt="" className="h-12 w-12 flex-none rounded-sm object-cover" />
                        ) : (
                          <div className="h-12 w-12 flex-none rounded-sm bg-surface-2" />
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold text-ink-900">{r.item?.title ?? '—'}</p>
                          <p className="text-[12.5px] text-ink-500">
                            {[r.item?.variant, r.item ? `Qty ${r.item.qty}` : null].filter(Boolean).join(' · ')}
                          </p>
                          {r.item?.seller && (
                            <p className="text-[12.5px] text-ink-500">Seller: {r.item.seller.name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-ink-700">
                      <p className="font-semibold text-ink-900">{r.customer?.name || `Customer #${r.customer?.id ?? '—'}`}</p>
                      {r.customer?.phone && (
                        <a href={`tel:${r.customer.phone}`} className="flex items-center gap-1 text-[12.5px] text-ink-500 hover:text-admin-ink">
                          <Phone size={13} /> {r.customer.phone}
                        </a>
                      )}
                      {r.customer?.email && (
                        <a href={`mailto:${r.customer.email}`} className="flex items-center gap-1 text-[12.5px] text-ink-500 hover:text-admin-ink">
                          <Mail size={13} /> {r.customer.email}
                        </a>
                      )}
                    </td>
                    <td>
                      <p className="sku text-ink-900">{r.order?.orderNumber ?? '—'}</p>
                      {r.order && (
                        <p className="text-[12.5px] text-ink-500">
                          {new Date(r.order.placedAt).toLocaleDateString('en-IN')} · <span className="font-tabular">{inr(r.order.totalAmount)}</span> paid
                        </p>
                      )}
                      {r.payment?.gatewayPaymentId && (
                        <p className="sku text-ink-500">{r.payment.gatewayPaymentId}</p>
                      )}
                    </td>
                    <td className="text-ink-700">
                      <p className="font-semibold">{SOURCE_LABEL[r.source]}</p>
                      {r.reason && <p className="max-w-[220px] text-[12.5px] text-ink-500">“{r.reason}”</p>}
                      <p className="text-[12.5px] text-ink-500">{new Date(r.createdAt).toLocaleString('en-IN')}</p>
                    </td>
                    <td className="text-right">
                      <p className="font-tabular font-semibold text-ink-900">
                        {inr(r.refundedAmount ?? r.suggestedAmount)}
                      </p>
                      {r.refundedAmount == null && <p className="text-[12.5px] text-ink-500">Suggested</p>}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_STYLE[r.status] ?? ''}`}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </span>
                      {r.failureReason && <p className="mt-1 max-w-[200px] text-[12.5px] text-danger-ink">{r.failureReason}</p>}
                      {r.gatewayRefundId && <p className="sku mt-1 text-ink-500">{r.gatewayRefundId}</p>}
                      {r.processedAt && (
                        <p className="mt-1 text-[12.5px] text-ink-500">{new Date(r.processedAt).toLocaleString('en-IN')}</p>
                      )}
                      {r.adminNote && <p className="mt-1 max-w-[200px] text-[12.5px] text-ink-500">{r.adminNote}</p>}
                    </td>
                    <td className="text-right">
                      {r.status === 'PENDING' || r.status === 'FAILED' ? (
                        <button
                          type="button"
                          onClick={() => setActive(r)}
                          className="btn btn-tonal btn-sm"
                        >
                          {r.status === 'FAILED' ? <RefreshCw size={16} /> : <IndianRupee size={16} />}
                          {r.status === 'FAILED' ? 'Retry refund' : 'Refund'}
                        </button>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <Pagination page={page} totalPages={totalPages} totalItems={filtered.length} perPage={PER_PAGE} onPageChange={setPage} />
        )}
      </div>

      {active && (
        <RefundDialog
          row={active}
          onClose={() => setActive(null)}
          onDone={async () => {
            setActive(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/** Confirm step: shows who gets what, lets the admin adjust the amount. */
function RefundDialog({ row, onClose, onDone }: { row: RefundRow; onClose: () => void; onDone: () => Promise<void> }) {
  const remaining = row.payment?.remainingRefundable ?? row.suggestedAmount;
  const [amount, setAmount] = useState(String(Math.min(row.suggestedAmount, remaining)));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const value = Number(amount);
  const invalid = !(value > 0) || value > remaining;

  const submit = async () => {
    if (invalid) return;
    setBusy(true);
    try {
      const { data } = await api.post<{ simulated?: boolean }>(`/admin/refunds/${row.id}/refund`, {
        amount: value,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(data.simulated ? `Refund of ${inr(value)} recorded (simulated — no Razorpay keys)` : `Refund of ${inr(value)} sent to Razorpay`);
      await onDone();
    } catch (err) {
      toast.error(extractErr(err));
      // A failed attempt is saved as FAILED with the gateway's reason.
      await onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Refund customer"
      description={
        <>
          {row.item?.title}{row.item?.variant ? ` · ${row.item.variant}` : ''} — order{' '}
          <span className="sku">{row.order?.orderNumber}</span>
        </>
      }
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || invalid}
            className="btn btn-primary"
          >
            {busy ? 'Sending…' : `Refund ${invalid ? '' : inr(value)}`}
          </button>
        </>
      }
    >
      <dl className="mb-4 space-y-1.5 rounded-md bg-surface-sunk p-4 text-[13.5px]">
        <div className="flex justify-between gap-4"><dt className="text-ink-500">Customer</dt><dd className="text-ink-900">{row.customer?.name || '—'} · {row.customer?.phone}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-ink-500">Item total (incl. tax)</dt><dd className="font-tabular font-semibold text-ink-900">{inr(row.item?.lineTotal ?? 0)}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-ink-500">Suggested</dt><dd className="font-tabular font-semibold text-ink-900">{inr(row.suggestedAmount)}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-ink-500">Still refundable on payment</dt><dd className="font-tabular font-semibold text-ink-900">{inr(remaining)}</dd></div>
      </dl>

      <label className="field-label mb-1.5" htmlFor="refund-amount">Amount to refund (₹)</label>
      <input
        id="refund-amount"
        type="number"
        min={0}
        step="0.01"
        max={remaining}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className={`input font-tabular ${invalid ? 'is-invalid' : ''}`}
      />
      {invalid && (
        <p className="field-error mt-1">
          Enter an amount between ₹0 and {inr(remaining)}.
        </p>
      )}

      <label className="field-label mb-1.5 mt-4" htmlFor="refund-note">Note (optional)</label>
      <textarea
        id="refund-note"
        rows={2}
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. Delivery fee included — whole order rejected"
        className="input"
      />

      <p className="field-help mt-3">
        The money goes back to the customer's original payment method through Razorpay. This can't be undone.
      </p>
    </Modal>
  );
}
